<template>
  <div class="notification-settings">
    <div class="alert alert-info">
      <font-awesome-icon icon="info-circle" />
      <div>
        <strong>Benachrichtigungskanäle:</strong>
        <ul>
          <li><strong><font-awesome-icon icon="envelope" /> E-Mail:</strong> Benachrichtigungen per E-Mail erhalten</li>
          <li><strong><font-awesome-icon icon="bell" /> In-App:</strong> Benachrichtigungen in der Anwendung anzeigen</li>
          <li><strong><font-awesome-icon icon="mobile-alt" /> Push:</strong> Push-Benachrichtigungen auf deinem Gerät erhalten</li>
        </ul>
      </div>
    </div>

    <form @submit.prevent="saveSettings" v-if="settings">
      <!-- E-Mail-Frequenz-Einstellung -->
      <div class="email-frequency-section">
        <div class="section-header email-frequency-header">
          <h3><font-awesome-icon icon="clock" /> E-Mail-Frequenz</h3>
          <p>Wann sollen E-Mail-Benachrichtigungen versendet werden?</p>
        </div>

        <div class="email-frequency-content">
          <div class="frequency-options">
            <div class="frequency-option">
              <label class="radio-container">
                <input 
                  type="radio" 
                  v-model="settings.emailFrequency"
                  value="immediate"
                  :disabled="loading"
                />
                <span class="radio-mark"></span>
                <div class="option-content">
                  <strong>Sofort</strong>
                  <small>E-Mails werden sofort bei jeder Benachrichtigung versendet</small>
                </div>
              </label>
            </div>
            <div class="frequency-option">
              <label class="radio-container">
                <input 
                  type="radio" 
                  v-model="settings.emailFrequency"
                  value="daily"
                  :disabled="loading"
                />
                <span class="radio-mark"></span>
                <div class="option-content">
                  <strong>Täglich</strong>
                  <small>E-Mails werden als tägliche Zusammenfassung versendet</small>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="notification-matrix">
        <!-- Matrix Header -->
        <div class="matrix-header">
          <div class="notification-type-column">
            <h3>Benachrichtigungstyp</h3>
            <p class="matrix-description">Klicke auf einen Benachrichtigungstyp oder Kanal, um alle entsprechenden Einstellungen zu aktivieren/deaktivieren</p>
          </div>
          <div class="channel-columns">
            <div class="channel-header clickable" @click="toggleChannel('email')">
              <font-awesome-icon icon="envelope" />
              <span>E-Mail</span>
            </div>
            <div class="channel-header clickable" @click="toggleChannel('inApp')">
              <font-awesome-icon icon="bell" />
              <span>In-App</span>
            </div>
            <div class="channel-header clickable" @click="toggleChannel('push')">
              <font-awesome-icon icon="mobile-alt" />
              <span>Push</span>
            </div>
          </div>
        </div>

        <!-- Global Controls -->
        <div class="global-controls">
          <button 
            type="button" 
            class="btn btn-outline-primary btn-sm"
            @click="toggleAllNotifications(true)"
            :disabled="loading"
          >
            <font-awesome-icon icon="check-double" />
            Alle anschalten
          </button>
          <button 
            type="button" 
            class="btn btn-outline-secondary btn-sm"
            @click="toggleAllNotifications(false)"
            :disabled="loading"
          >
            <font-awesome-icon icon="times" />
            Alle ausschalten
          </button>
        </div>

        <!-- Matrix Rows -->
        <div class="matrix-rows">
          <!-- Benutzer-Benachrichtigungen -->
          <div class="section-header">
            <h3><font-awesome-icon icon="user" /> Benutzer-Benachrichtigungen</h3>
            <p>Benachrichtigungen für alle Benutzer</p>
          </div>

          <!-- Quittungsstatus geändert -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('receiptStatusChanged')">
              <h4>Quittungsstatus geändert</h4>
              <p>Status deiner Quittung hat sich geändert (bezahlt, vorbereitet, offen)</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.receiptStatusChanged.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.receiptStatusChanged.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.receiptStatusChanged.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Aus Team entfernt -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('userRemovedFromTeam')">
              <h4>Aus Bereich entfernt</h4>
              <p>Du wurdest aus einem Team entfernt</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userRemovedFromTeam.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userRemovedFromTeam.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userRemovedFromTeam.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Quittung abgelehnt -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('receiptRejected')">
              <h4>Quittung abgelehnt</h4>
              <p>Deine Quittung wurde abgelehnt</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.receiptRejected.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.receiptRejected.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.receiptRejected.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Team neues Pflichtfeld -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('teamFieldRequired')">
              <h4>Team neues Pflichtfeld</h4>
              <p>Neues Pflichtfeld in deinem Team hinzugefügt</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamFieldRequired.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamFieldRequired.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamFieldRequired.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Team Erstattung aktiviert -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('teamRefundActivated')">
              <h4>Team Erstattung aktiviert</h4>
              <p>Erstattung in deinem Team wurde aktiviert</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamRefundActivated.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamRefundActivated.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamRefundActivated.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Beitrittsanfrage angenommen oder abgelehnt -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('teamRequestResponse')">
              <h4>Beitrittsanfrage angenommen oder abgelehnt</h4>
              <p>Deine Anfrage zum Teambeitritt wurde bearbeitet</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamRequestResponse.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamRequestResponse.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamRequestResponse.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Newsletter -->
          <div class="matrix-row newsletter-row">
            <div class="notification-info clickable" @click="toggleNotificationType('newsletter')">
              <h4>Newsletter</h4>
              <p>Newsletter und wichtige Updates von Quitly</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.newsletter.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <div class="disabled-channel">
                <font-awesome-icon icon="ban" />
              </div>
              <div class="disabled-channel">
                <font-awesome-icon icon="ban" />
              </div>
            </div>
          </div>

          <!-- Admin-Beförderung -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('userPromotedToAdmin')">
              <h4>Zum Administrator befördert</h4>
              <p>Du wurdest zum Team-Administrator befördert</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userPromotedToAdmin.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userPromotedToAdmin.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userPromotedToAdmin.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Admin-Degradierung -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('userDemotedFromAdmin')">
              <h4>Administrator-Rechte entzogen</h4>
              <p>Deine Administrator-Rechte wurden entzogen</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userDemotedFromAdmin.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userDemotedFromAdmin.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userDemotedFromAdmin.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Team gelöscht -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('teamDeleted')">
              <h4>Team wurde gelöscht</h4>
              <p>Ein Team, dem du angehörst, wurde gelöscht</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamDeleted.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamDeleted.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamDeleted.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>


          <!-- Admin-Benachrichtigungen -->
          <div class="section-header admin-section">
            <h3><font-awesome-icon icon="user-shield" /> Admin-Benachrichtigungen</h3>
            <p>Benachrichtigungen nur für Team-Administratoren</p>
          </div>

          <!-- Team Quittung erstellt -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('teamReceiptCreated')">
              <h4>Team Quittung erstellt</h4>
              <p>Neue Quittung in deinem Team erstellt</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamReceiptCreated.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamReceiptCreated.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamReceiptCreated.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Quittungs-Transfer -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('coinTransfer')">
              <h4>Quittungs-Transfer</h4>
              <p>Benachrichtigungen über Quittungs-Transfers im Team (Auffüllen/Entnehmen)</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.coinTransfer.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.coinTransfer.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.coinTransfer.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Quittung storniert -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('receiptCancelled')">
              <h4>Quittung storniert</h4>
              <p>Eine Quittung wurde storniert</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.receiptCancelled.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.receiptCancelled.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.receiptCancelled.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- Teamanfrage -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('teamRequest')">
              <h4>Teamanfrage</h4>
              <p>Jemand möchte deinem Team beitreten</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamRequest.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamRequest.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.teamRequest.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- User hat Team verlassen -->
          <div class="matrix-row">
            <div class="notification-info clickable" @click="toggleNotificationType('userLeftTeam')">
              <h4>User hat Team verlassen</h4>
              <p>Ein Benutzer hat dein Team verlassen</p>
            </div>
            <div class="channel-checkboxes">
              <label class="checkbox-container">
                <font-awesome-icon icon="envelope" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userLeftTeam.email"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="bell" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userLeftTeam.inApp"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
              <label class="checkbox-container">
                <font-awesome-icon icon="mobile-alt" class="channel-icon" />
                <input 
                  type="checkbox" 
                  v-model="settings.userLeftTeam.push"
                  :disabled="loading"
                />
                <span class="checkmark"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button 
          type="submit" 
          class="btn btn-primary"
          :disabled="loading || !hasChanges"
        >
          <font-awesome-icon icon="save" v-if="!loading" />
          <font-awesome-icon icon="spinner" spin v-if="loading" />
          {{ loading ? 'Speichern...' : 'Einstellungen speichern' }}
        </button>
        
        <button 
          type="button" 
          class="btn btn-secondary"
          @click="resetSettings"
          :disabled="loading || !hasChanges"
        >
          <font-awesome-icon icon="undo" />
          Zurücksetzen
        </button>
      </div>
    </form>


    <div v-if="loading && !settings" class="loading-state">
      <font-awesome-icon icon="spinner" spin size="2x" />
      <p>Lade Einstellungen...</p>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { getNotificationSettings, updateNotificationSettings } from '@/api/notifications'
import { showSuccess, showError, showConfirmDialogWithDeny } from '@/services/notification'

export default {
  name: 'NotificationSettings',
  setup() {
    const settings = ref(null)
    const originalSettings = ref(null)
    const loading = ref(false)
    const hasUnsavedChanges = ref(false)

    const hasChanges = computed(() => {
      if (!settings.value || !originalSettings.value) return false
      
      return JSON.stringify(settings.value) !== JSON.stringify(originalSettings.value)
    })

    const checkForChanges = () => {
      if (!settings.value || !originalSettings.value) return false
      
      return JSON.stringify(settings.value) !== JSON.stringify(originalSettings.value)
    }

    const confirmBeforeLeaving = async () => {
      if (!hasUnsavedChanges.value) {
        return true
      }
      
      const result = await showConfirmDialogWithDeny(
        'Ungespeicherte Änderungen',
        'Du hast ungespeicherte Änderungen. Möchtest du diese speichern, bevor du fortfährst?',
        'Speichern',
        'Verwerfen',
        'Abbrechen',
        {
          icon: 'warning',
          confirmButtonColor: '#0d6efd',
          denyButtonColor: '#dc3545',
          cancelButtonColor: '#6c757d'
        }
      )
      
      if (result.isConfirmed) {
        // Speichere die Änderungen vor dem Verlassen
        await saveSettings()
        return true
      } else if (result.isDenied) {
        // Verwerfe die Änderungen
        hasUnsavedChanges.value = false
        return true
      } else {
        // Benutzer hat abgebrochen
        return false
      }
    }

    const loadSettings = async () => {
      try {
        loading.value = true
        const response = await getNotificationSettings()
        settings.value = response.data.data
        
        
        if (settings.value.newsletter) {
          settings.value.newsletter = {
            email: settings.value.newsletter.email || false
          }
        }
        
        // Coin-Transfer Einstellungen initialisieren falls nicht vorhanden
        if (!settings.value.coinTransfer) {
          settings.value.coinTransfer = {
            email: true,
            inApp: true,
            push: true
          }
        }
        
        // Neue Admin- und Team-Benachrichtigungen initialisieren falls nicht vorhanden
        if (!settings.value.userPromotedToAdmin) {
          settings.value.userPromotedToAdmin = {
            email: true,
            inApp: true,
            push: true
          }
        }
        
        if (!settings.value.userDemotedFromAdmin) {
          settings.value.userDemotedFromAdmin = {
            email: true,
            inApp: true,
            push: true
          }
        }
        
        if (!settings.value.teamDeleted) {
          settings.value.teamDeleted = {
            email: true,
            inApp: true,
            push: true
          }
        }
        
        originalSettings.value = JSON.parse(JSON.stringify(settings.value))
      } catch (error) {
        console.error('Fehler beim Laden der Einstellungen:', error)
        showError('Fehler beim Laden der Benachrichtigungseinstellungen')
      } finally {
        loading.value = false
      }
    }

    const saveSettings = async () => {
      try {
        loading.value = true
        
        
        const cleanedSettings = { ...settings.value }
        
        
        if (cleanedSettings.newsletter) {
          cleanedSettings.newsletter = {
            email: cleanedSettings.newsletter.email || false
          }
        }
        
        // Coin-Transfer Einstellungen sicherstellen
        if (cleanedSettings.coinTransfer) {
          cleanedSettings.coinTransfer = {
            email: cleanedSettings.coinTransfer.email || false,
            inApp: cleanedSettings.coinTransfer.inApp || false,
            push: cleanedSettings.coinTransfer.push || false
          }
        }
        
        // Neue Admin- und Team-Benachrichtigungen sicherstellen
        if (cleanedSettings.userPromotedToAdmin) {
          cleanedSettings.userPromotedToAdmin = {
            email: cleanedSettings.userPromotedToAdmin.email || false,
            inApp: cleanedSettings.userPromotedToAdmin.inApp || false,
            push: cleanedSettings.userPromotedToAdmin.push || false
          }
        }
        
        if (cleanedSettings.userDemotedFromAdmin) {
          cleanedSettings.userDemotedFromAdmin = {
            email: cleanedSettings.userDemotedFromAdmin.email || false,
            inApp: cleanedSettings.userDemotedFromAdmin.inApp || false,
            push: cleanedSettings.userDemotedFromAdmin.push || false
          }
        }
        
        if (cleanedSettings.teamDeleted) {
          cleanedSettings.teamDeleted = {
            email: cleanedSettings.teamDeleted.email || false,
            inApp: cleanedSettings.teamDeleted.inApp || false,
            push: cleanedSettings.teamDeleted.push || false
          }
        }
        
        await updateNotificationSettings(cleanedSettings)
        originalSettings.value = JSON.parse(JSON.stringify(settings.value))
        hasUnsavedChanges.value = false
        showSuccess('Benachrichtigungseinstellungen erfolgreich gespeichert')
      } catch (error) {
        console.error('Fehler beim Speichern der Einstellungen:', error)
        showError('Fehler beim Speichern der Benachrichtigungseinstellungen')
      } finally {
        loading.value = false
      }
    }

    const resetSettings = () => {
      if (originalSettings.value) {
        settings.value = JSON.parse(JSON.stringify(originalSettings.value))
        hasUnsavedChanges.value = false
      }
    }

    const toggleAllNotifications = (enable) => {
      if (!settings.value) return
      
      const notificationTypes = [
        'receiptStatusChanged',
        'teamFieldRequired', 
        'teamRefundActivated',
        'newsletter',
        'teamReceiptCreated',
        'receiptCancelled',
        'teamRequest',
        'teamRequestResponse',
        'userRemovedFromTeam',
        'receiptRejected',
        'userLeftTeam',
        'coinTransfer',
        'userPromotedToAdmin',
        'userDemotedFromAdmin',
        'teamDeleted'
      ]
      
      notificationTypes.forEach(type => {
        if (settings.value[type]) {
          if (type === 'newsletter') {
            
            settings.value[type].email = enable
          } else {
            
            settings.value[type].email = enable
            settings.value[type].inApp = enable
            settings.value[type].push = enable
          }
        }
      })
    }

    const toggleNotificationType = (type) => {
      if (!settings.value || !settings.value[type]) return
      
      
      const isAllEnabled = type === 'newsletter' 
        ? settings.value[type].email
        : settings.value[type].email && settings.value[type].inApp && settings.value[type].push
      
      const enable = !isAllEnabled
      
      if (type === 'newsletter') {
        
        settings.value[type].email = enable
      } else {
        
        settings.value[type].email = enable
        settings.value[type].inApp = enable
        settings.value[type].push = enable
      }
    }

    const toggleChannel = (channel) => {
      if (!settings.value) return
      
      const notificationTypes = [
        'receiptStatusChanged',
        'teamFieldRequired', 
        'teamRefundActivated',
        'newsletter',
        'teamReceiptCreated',
        'receiptCancelled',
        'teamRequest',
        'teamRequestResponse',
        'userRemovedFromTeam',
        'receiptRejected',
        'userLeftTeam',
        'coinTransfer',
        'userPromotedToAdmin',
        'userDemotedFromAdmin',
        'teamDeleted'
      ]
      
      
      const isAllEnabled = notificationTypes.every(type => {
        if (!settings.value[type]) return true
        if (type === 'newsletter') {
          return channel === 'email' ? settings.value[type].email : true
        }
        return settings.value[type][channel] || false
      })
      
      const enable = !isAllEnabled
      
      notificationTypes.forEach(type => {
        if (settings.value[type]) {
          if (type === 'newsletter') {
            
            if (channel === 'email') {
              settings.value[type].email = enable
            }
          } else {
            
            settings.value[type][channel] = enable
          }
        }
      })
    }


    // Watcher für Änderungen
    watch(settings, () => {
      hasUnsavedChanges.value = checkForChanges()
    }, { deep: true })

    // Router-Guard für Navigation
    onBeforeRouteLeave(async (to, from, next) => {
      if (!hasUnsavedChanges.value) {
        next()
        return
      }
      
      const result = await showConfirmDialogWithDeny(
        'Ungespeicherte Änderungen',
        'Du hast ungespeicherte Änderungen. Möchtest du diese speichern, bevor du fortfährst?',
        'Speichern',
        'Verwerfen',
        'Abbrechen',
        {
          icon: 'warning',
          confirmButtonColor: '#0d6efd',
          denyButtonColor: '#dc3545',
          cancelButtonColor: '#6c757d'
        }
      )
      
      if (result.isConfirmed) {
        // Speichere die Änderungen vor dem Verlassen
        try {
          await saveSettings()
          next()
        } catch (error) {
          console.error('Fehler beim Speichern:', error)
          next(false) // Verhindere Navigation bei Fehler
        }
      } else if (result.isDenied) {
        // Verwerfe die Änderungen und erlaube Navigation
        hasUnsavedChanges.value = false
        next()
      } else {
        // Benutzer hat abgebrochen - verhindere Navigation
        next(false)
      }
    })

    // Browser beforeunload Handler
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges.value) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    onMounted(() => {
      loadSettings()
      window.addEventListener('beforeunload', handleBeforeUnload)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    })

    return {
      settings,
      loading,
      hasChanges,
      saveSettings,
      resetSettings,
      toggleAllNotifications,
      toggleNotificationType,
      toggleChannel
    }
  }
}
</script>

<style scoped>


.settings-header {
  text-align: center;
  margin-bottom: 2rem;
}

.settings-header h2 {
  color: #1e293b;
  margin-bottom: 0.5rem;
  font-size: 2rem;
  font-weight: 700;
}

.settings-subtitle {
  color: #64748b;
  font-size: 1.1rem;
  margin: 0;
}

.notification-matrix {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
}

.matrix-header {
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  padding: 1.5rem;
  font-weight: 600;
  color: #374151;
}

.notification-type-column h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #1e293b;
}

.channel-columns {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
}

.channel-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #64748b;
}

.channel-header svg {
  font-size: 1.1rem;
}

.channel-header span {
  font-size: 0.8rem;
  font-weight: 500;
}

.matrix-description {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0.5rem 0 0 0;
  font-style: italic;
}

.global-controls {
  display: flex;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  justify-content: center;
}

.global-controls .btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.clickable {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 8px;
  padding: 0.5rem;
  margin: -0.5rem;
}

.clickable:hover {
  background-color: #f1f5f9;
}

.matrix-rows {
  display: flex;
  flex-direction: column;
}

.section-header {
  background: #f1f5f9;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  margin: 0;
  margin-top: 1rem;
}

.section-header:first-child {
  margin-top: 0;
}

.section-header.admin-section {
  background: #fef3c7;
  border-bottom-color: #f59e0b;
}

.email-frequency-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
}

.email-frequency-header {
  background: #f0f9ff;
  border-bottom-color: #0ea5e9;
  margin-top: 0;
}

.email-frequency-content {
  padding: 2rem;
}

.frequency-options {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 600px;
}

.section-header h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.3rem;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-header p {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
}

.matrix-row {
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr;
  padding: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  align-items: center;
  transition: background-color 0.2s;
  margin-bottom: 0.5rem;
}

.matrix-row:last-child {
  margin-bottom: 0;
}

.matrix-row:hover {
  background-color: #f8fafc;
}

.matrix-row.newsletter-row {
  background-color: #f8fafc;
}

.notification-info h4 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
  font-size: 1.1rem;
  font-weight: 600;
}

.notification-info p {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.4;
}

.channel-checkboxes {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  justify-items: center;
}

.checkbox-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 24px;
  height: 24px;
}

.channel-icon {
  display: none;
  margin-right: 0.5rem;
  color: #64748b;
  font-size: 0.9rem;
}

.checkbox-container input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  margin: 0;
}

.checkmark {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  background: white;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: #4a86e8;
  border-color: #4a86e8;
}

.checkbox-container input:checked ~ .checkmark::after {
  content: "✓";
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.checkbox-container input:disabled ~ .checkmark {
  opacity: 0.5;
  cursor: not-allowed;
}

.disabled-channel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 0.8rem;
  gap: 0.25rem;
}

.disabled-channel svg {
  font-size: 1rem;
}


.frequency-option {
  display: flex;
  align-items: flex-start;
}

.radio-container {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  width: 100%;
}

.radio-container input[type="radio"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  width: 0;
  height: 0;
  margin: 0;
}

.radio-mark {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  background: white;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.radio-container input:checked ~ .radio-mark {
  background-color: #4a86e8;
  border-color: #4a86e8;
}

.radio-container input:checked ~ .radio-mark::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
}

.radio-container input:disabled ~ .radio-mark {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-content {
  flex: 1;
}

.option-content strong {
  display: block;
  color: #1e293b;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.option-content small {
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.4;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 2rem;
  background: #f8fafc;
  border-radius: 8px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #4a86e8;
  color: white;
  box-shadow: 0 2px 4px rgba(74, 134, 232, 0.2);
}

.btn-primary:hover:not(:disabled) {
  background-color: #3b6bc7;
  box-shadow: 0 4px 8px rgba(74, 134, 232, 0.3);
  transform: translateY(-1px);
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5a6268;
  transform: translateY(-1px);
}

.alert {
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.alert-info {
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  color: #0066cc;
}

.alert-info strong {
  color: #1e40af;
}

.alert-info ul {
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
}

.alert-info li {
  margin-bottom: 0.25rem;
}

.loading-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
}

.loading-state svg {
  color: #4a86e8;
  margin-bottom: 1rem;
}

@media (max-width: 1024px) {
  .matrix-header,
  .matrix-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .channel-columns,
  .channel-checkboxes {
    grid-template-columns: 1fr 1fr 1fr;
  }
  
  .notification-info {
    text-align: center;
  }
  
  .channel-icon {
    display: inline-block;
  }
  
  .checkbox-container {
    width: auto;
    justify-content: flex-start;
  }
}

@media (min-width: 1025px) {
  .matrix-header,
  .matrix-row {
    grid-template-columns: 3fr 1fr 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .notification-settings {
    padding: 1rem;
  }
  
  .settings-header h2 {
    font-size: 1.5rem;
  }
  
  .matrix-header,
  .matrix-row {
    padding: 1rem;
  }
  
  .channel-columns,
  .channel-checkboxes {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .form-actions {
    flex-direction: column;
    padding: 1rem;
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
